import React, { useState, useEffect } from "react";
import {
  User,
  CreateUserData,
  EditUserData,
  ExtendedUser,
  Company,
} from "../../store/types";
import { validateField, formatPhone } from "../../hooks/validation";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import CustomInputText from "../CustomInput/CustomInputText";
import CustomDropdown from "../CustomInput/CustomDropdown";
import CustomInputMask from "../CustomInput/CustomInputMask";
import { StyledIconLine } from "../StyledIcon/StyledIcon";
import { DangerText } from "../../styles/Typography";
import {
  IconUser,
  IconCompany,
  IconMail,
  IconPhone,
  IconUserFilled,
  IconCompanyFilled,
  IconMailFilled,
  IconPhoneFilled,
} from "../../assets/icons/Icon";
import { Column, SmallColumn } from "./Dialogs.styles";

interface DialogProps {
  visible: boolean;
  onHide: () => void;
  dialogType: string;
  user?: ExtendedUser | null;
  company?: Company;
  handleAddUser?: (
    user: CreateUserData
  ) => Promise<{ success: boolean; user?: User; token?: string }>;
  handleEditUser?: (user: EditUserData) => Promise<boolean>;
  handleDeleteUser?: (user: User) => Promise<boolean>;
  refetchData?: () => {};
  companiesOptions?: { label: string; value: any }[];
}

const initialFormData: CreateUserData = {
  lastName: "",
  name: "",
  patronymic: "",
  email: "",
  phone: "",
  organizationId: 0,
};

const initialErrors: Record<keyof CreateUserData, boolean> = {
  lastName: false,
  name: false,
  patronymic: false,
  email: false,
  phone: false,
  organizationId: false,
};

const UserDialogs: React.FC<DialogProps> = ({
  visible,
  onHide,
  dialogType,
  user,
  company,
  handleAddUser,
  handleEditUser,
  handleDeleteUser,
  refetchData,
  companiesOptions = [],
}) => {
  const [formData, setFormData] = useState<CreateUserData>(initialFormData);
  const [errors, setErrors] =
    useState<Record<keyof CreateUserData, boolean>>(initialErrors);
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (field: keyof CreateUserData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    const newErrors: Record<keyof CreateUserData, boolean> = {
      lastName: !validateField("lastName", formData.lastName),
      name: !validateField("name", formData.name),
      patronymic: !validateField("patronymic", formData.patronymic),
      email: !validateField("email", formData.email),
      phone: !validateField("phone", formData.phone),
      organizationId: !validateField("organizationId", formData.organizationId),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleBlur = (field: keyof CreateUserData) => {
    const value = formData[field];
    const isValid = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (dialogType !== "delete")
      if (!validateAllFields()) {
        setValidForm(false);
        return;
      }

    setValidForm(true);
    setSubmitting(true);
    let success = false;
    let response;

    try {
      if (dialogType === "create" && handleAddUser) {
        response = await handleAddUser!(formData);
        success = response.success;
      } else if (dialogType === "edit" && handleEditUser) {
        success = await handleEditUser!({ ...formData, id: user!.id });
      } else if (dialogType === "delete" && handleDeleteUser) {
        success = await handleDeleteUser!(user!);
      }
      if (success) {
        onHide();
        refetchData!();
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setValidForm(true);
      if (dialogType === "edit" && user) {
        const newData = {
          lastName: user.lastName || "",
          name: user.name || "",
          patronymic: user.patronymic || "",
          email: user.email || "",
          phone: user.phone
            ? String(user.phone).replace(/\D/g, "").slice(-10)
            : user.phone,
          organizationId: user.organizationId ?? 0,
        };
        setFormData(newData);
        const newErrors: Record<keyof CreateUserData, boolean> = {
          lastName: !validateField("lastName", newData.lastName),
          name: !validateField("name", newData.name),
          patronymic: !validateField("patronymic", newData.patronymic),
          email: !validateField("email", newData.email),
          phone: !validateField("phone", newData.phone),
          organizationId: !validateField(
            "organizationId",
            newData.organizationId
          ),
        };
        setErrors(newErrors);
      } else if (dialogType === "create") {
        setFormData({ ...initialFormData, organizationId: company?.id ?? 0 });
        setErrors(initialErrors);
      } else {
        setFormData(initialFormData);
        setErrors(initialErrors);
      }
    }
  }, [visible, dialogType, user]);

  useEffect(() => {
    if (!visible) {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setValidForm(true);
      setSubmitting(false);
    }
  }, [visible]);

  const getHeader = (type: string) => {
    switch (type) {
      case "create":
        return "Новый пользователь";
      case "edit":
        return "Редактировать пользователя";
      case "delete":
        return "Удалить пользователя";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const footerContent = (
    <div>
      <Button label="Отмена" onClick={onHide} outlined />
      <Button
        label={
          dialogType === "delete"
            ? "Удалить"
            : dialogType === "edit"
            ? "Сохранить"
            : "Создать"
        }
        onClick={handleSubmit}
        loading={submitting}
      />
    </div>
  );

  const formContent = (
    <Column>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      <CustomInputText
        value={formData.lastName}
        setValue={(val) => handleFieldChange("lastName", val)}
        onBlur={() => handleBlur("lastName")}
        placeholder="Фамилия"
        icon={<IconUser size={20} />}
        invalid={errors.lastName}
      />
      <CustomInputText
        value={formData.name}
        setValue={(val) => handleFieldChange("name", val)}
        onBlur={() => handleBlur("name")}
        placeholder="Имя"
        icon={<IconUser size={20} />}
        invalid={errors.name}
      />
      <CustomInputText
        value={formData.patronymic}
        setValue={(val) => handleFieldChange("patronymic", val)}
        onBlur={() => handleBlur("patronymic")}
        placeholder="Отчество"
        icon={<IconUser size={20} />}
        invalid={errors.patronymic}
      />
      {dialogType === "create" && company ? (
        <></>
      ) : (
        <CustomDropdown
          value={formData.organizationId}
          onChange={(val) => handleFieldChange("organizationId", val)}
          onBlur={() => handleBlur("organizationId")}
          options={companiesOptions}
          placeholder="Компания"
          icon={<IconCompany size={20} />}
          invalid={errors.organizationId}
          filter
        />
      )}
      <CustomInputText
        value={formData.email}
        setValue={(val) => handleFieldChange("email", val)}
        onBlur={() => handleBlur("email")}
        placeholder="Почта"
        icon={<IconMail size={20} />}
        invalid={errors.email}
      />
      <CustomInputMask
        value={formData.phone}
        setValue={(val) => handleFieldChange("phone", val)}
        onBlur={() => handleBlur("phone")}
        mask="+7 (999) 999-99-99"
        unmask={true}
        placeholder="+7 (___) ___-__-__"
        icon={<IconPhone size={20} />}
        invalid={errors.phone}
      />
    </Column>
  );

  const deleteForm = (
    <Column>
      <DangerText>
        Вы уверены, что хотите удалить данного пользователя?
      </DangerText>
      <SmallColumn>
        <StyledIconLine
          icon={<IconUserFilled size={20} />}
          text={
            user?.fullName ||
            `${user?.lastName} ${user?.name} ${user?.patronymic}`
          }
        />
        <StyledIconLine
          icon={<IconCompanyFilled size={20} />}
          text={`${user?.organizationName ?? "Нет данных"}`}
        />
        <StyledIconLine
          icon={<IconMailFilled size={20} />}
          text={user?.email ?? "Нет данных"}
        />
        <StyledIconLine
          icon={<IconPhoneFilled size={20} />}
          text={
            user
              ? formatPhone(user!.phone) == ""
                ? "Нет данных"
                : formatPhone(user!.phone)
              : "Нет данных"
          }
        />
      </SmallColumn>
    </Column>
  );

  const getContent = (type: string) => {
    switch (type) {
      case "create":
        return formContent;
      case "edit":
        return formContent;
      case "delete":
        return deleteForm;
      default:
        return <></>;
    }
  };

  return (
    <Dialog
      header={getHeader(dialogType)}
      visible={visible}
      style={{ width: "500px" }}
      onHide={onHide}
      footer={footerContent}
    >
      {getContent(dialogType)}
    </Dialog>
  );
};

export default UserDialogs;
